import { Injectable } from "@angular/core";
import { map, Observable } from "rxjs";
import { HttpHeaders, HttpParams } from "@angular/common/http";

import { AuthService } from "./auth.service";
import { HttpService } from "./http.service";
import { AppConfig } from "../../app.config";
import {
  GatherResponse,
  GatherSamples,
  RawHeadersResponse,
  RawTraceHeadersResponse,
  SegyAutomuteParams,
  SegyAutomuteResponse,
  SegyBucketsResponse,
  SegyDirListing,
  SegyFileInfo,
  SegyGatherHeadersResponse,
  SegyGatherParams,
  SegyModelGeometryResponse,
  SegyScanResponse,
  SegySpectrumResponse,
} from "../../models/segy";

/**
 * Path-driven client for the standalone single SEG-Y file endpoints
 * (`/api/v1/segy/...`). These address a file directly by `path` + `is_aws`,
 * so a raw input can be inspected without any registered run.
 */
@Injectable({ providedIn: "root" })
export class SegyService {
  constructor(private http: HttpService, private authService: AuthService) {}

  private get baseUrl(): string {
    return `${AppConfig.settings.apiUrl}/segy`;
  }

  getInfo(path: string, isAws: boolean): Observable<SegyFileInfo> {
    return this.http.get(`${this.baseUrl}/info/`, {
      headers: this.getAuthorizationHeader(),
      params: this.pathParams(path, isAws),
    });
  }

  /** List the sub-folders and SEG-Y files of a directory, to find a raw input
   * without typing its full path (no registered run required). */
  listDir(path: string, isAws: boolean): Observable<SegyDirListing> {
    return this.http.get(`${this.baseUrl}/listdir/`, {
      headers: this.getAuthorizationHeader(),
      params: this.pathParams(path, isAws),
    });
  }

  getHeaders(path: string, isAws: boolean): Observable<RawHeadersResponse> {
    return this.http.get(`${this.baseUrl}/headers/`, {
      headers: this.getAuthorizationHeader(),
      params: this.pathParams(path, isAws),
    });
  }

  getTraceHeaders(
    path: string,
    isAws: boolean,
    offset: number,
    limit: number,
    allFields = false,
    custom: string[] = []
  ): Observable<RawTraceHeadersResponse> {
    let params = this.pathParams(path, isAws)
      .set("offset", offset.toString())
      .set("limit", limit.toString());
    if (allFields) params = params.set("all_fields", "true");
    if (custom.length) params = params.set("custom", custom.join(","));
    return this.http.get(`${this.baseUrl}/trace_headers/`, {
      headers: this.getAuthorizationHeader(),
      params,
    });
  }

  getGather(
    path: string,
    isAws: boolean,
    params: SegyGatherParams
  ): Observable<GatherResponse> {
    return this.http.get(`${this.baseUrl}/gather/`, {
      headers: this.getAuthorizationHeader(),
      params: this.gatherParams(path, isAws, params),
    });
  }

  /** The gather's (clipped-range) samples, for drawing it client-side as
   * cells or wiggles. Same params as `getGather`. */
  getGatherSamples(
    path: string,
    isAws: boolean,
    params: SegyGatherParams
  ): Observable<GatherSamples> {
    return this.http
      .get(`${this.baseUrl}/gather_samples/`, {
        headers: this.getAuthorizationHeader(),
        params: this.gatherParams(path, isAws, params),
        responseType: "arraybuffer",
      })
      .pipe(map((buffer: ArrayBuffer) => this.decodeSamples(buffer)));
  }

  /** uint32 LE JSON length, the JSON meta (padded to 4 bytes), int16 LE
   * samples — see substrata `encode_gather_samples`. */
  private decodeSamples(buffer: ArrayBuffer): GatherSamples {
    const length = new DataView(buffer).getUint32(0, true);
    const meta = JSON.parse(
      new TextDecoder().decode(new Uint8Array(buffer, 4, length))
    );
    return { meta, data: new Int16Array(buffer, 4 + length) };
  }

  getSpectrum(
    path: string,
    isAws: boolean,
    params: SegyGatherParams
  ): Observable<SegySpectrumResponse> {
    return this.http.get(`${this.baseUrl}/spectrum/`, {
      headers: this.getAuthorizationHeader(),
      params: this.gatherParams(path, isAws, params),
    });
  }

  /** Auto-picked top mute of a gather (from first breaks), on the
   * bandpassed traces when a filter is given. */
  getAutomute(
    path: string,
    isAws: boolean,
    params: SegyGatherParams & SegyAutomuteParams
  ): Observable<SegyAutomuteResponse> {
    return this.http.get(`${this.baseUrl}/automute/`, {
      headers: this.getAuthorizationHeader(),
      params: this.gatherParams(path, isAws, params),
    });
  }

  /** One sampled pass over the trace headers, feeding both the geometry map
   * and the header plots. `fields` adds standard or custom (`b197_int32`)
   * fields beyond the core set the scan always reads. */
  getScan(
    path: string,
    isAws: boolean,
    maxPoints: number,
    fields: string[] = [],
    applyScalars = true
  ): Observable<SegyScanResponse> {
    let params = this.pathParams(path, isAws)
      .set("max_points", maxPoints.toString())
      .set("apply_scalars", applyScalars ? "true" : "false");
    if (fields.length) params = params.set("fields", fields.join(","));
    return this.http.get(`${this.baseUrl}/scan/`, {
      headers: this.getAuthorizationHeader(),
      params,
    });
  }

  /** Header values of every trace of a gather (same addressing as
   * {@link getGather}) for the chosen fields. */
  getGatherHeaders(
    path: string,
    isAws: boolean,
    addressing: SegyGatherParams,
    fields: string[]
  ): Observable<SegyGatherHeadersResponse> {
    const params = this.gatherParams(path, isAws, addressing).set(
      "fields",
      fields.join(",")
    );
    return this.http.get(`${this.baseUrl}/gather_headers/`, {
      headers: this.getAuthorizationHeader(),
      params,
    });
  }

  /** Survey geometry (origin, bounding box, bin spacing, rotation) of a 3D
   * SEG-Y model / volume, derived from its trace headers. */
  getModelGeometry(
    path: string,
    isAws: boolean,
    sample = 50000
  ): Observable<SegyModelGeometryResponse> {
    const params = this.pathParams(path, isAws).set(
      "sample",
      sample.toString()
    );
    return this.http.get(`${this.baseUrl}/model_geometry/`, {
      headers: this.getAuthorizationHeader(),
      params,
    });
  }

  /** The S3 buckets available for browsing (+ accepted custom-field formats). */
  listBuckets(): Observable<SegyBucketsResponse> {
    return this.http.get(`${this.baseUrl}/buckets/`, {
      headers: this.getAuthorizationHeader(),
    });
  }

  /** path/is_aws + every defined gather/filter param. */
  private gatherParams(
    path: string,
    isAws: boolean,
    params: SegyGatherParams & SegyAutomuteParams
  ): HttpParams {
    let httpParams = this.pathParams(path, isAws);
    for (const [key, val] of Object.entries(params)) {
      if (val === undefined || val === null) continue;
      httpParams = httpParams.set(key, val.toString());
    }
    return httpParams;
  }

  private pathParams(path: string, isAws: boolean): HttpParams {
    return new HttpParams()
      .set("path", path)
      .set("is_aws", isAws ? "true" : "false");
  }

  private getAuthorizationHeader(): HttpHeaders {
    let headers = new HttpHeaders();
    headers = headers.append(
      "Authorization",
      this.authService.getTokenHeaderText()
    );
    return headers;
  }
}
